import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import {
  createLead,
  getLeads,
  updateLead,
  convertLead,
} from "../services/crmLeadsService";
import "../styles/crm.css";

const defaultForm = {
  lead_name: "",
  business_name: "",
  email: "",
  phone: "",
  industry: "",
  lead_source: "",
  estimated_value: "",
  rating: "cold",
  status: "new",
  qualification_notes: "",
};

const LeadsPage = () => {
  const { companyId } = useCompany();
  const [leads, setLeads] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [convertingId, setConvertingId] = useState("");
  const [message, setMessage] = useState("");

  const loadLeads = useCallback(async () => {
    if (!companyId) {
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const data = await getLeads(companyId);
      setLeads(data);
    } catch (error) {
      setMessage(error.message || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const handleFormChange = useCallback((field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  }, []);

  const resetForm = useCallback(() => {
    setForm(defaultForm);
    setEditingId("");
  }, []);

  const handleEdit = useCallback((lead) => {
    setEditingId(lead.id);
    setForm({
      lead_name: lead.lead_name || "",
      business_name: lead.business_name || "",
      email: lead.email || "",
      phone: lead.phone || "",
      industry: lead.industry || "",
      lead_source: lead.lead_source || "",
      estimated_value: String(lead.estimated_value || ""),
      rating: lead.rating || "cold",
      status: lead.status || "new",
      qualification_notes: lead.qualification_notes || "",
    });
    setMessage("");
  }, []);

  const handleSave = useCallback(async () => {
    if (!companyId || !form.lead_name.trim()) {
      return;
    }

    setSaving(true);
    setMessage("");

    try {
      const payload = {
        company_id: companyId,
        lead_name: form.lead_name.trim(),
        business_name: form.business_name.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        industry: form.industry.trim() || null,
        lead_source: form.lead_source.trim() || null,
        estimated_value: Number(form.estimated_value || 0),
        rating: form.rating,
        status: form.status,
        qualification_notes: form.qualification_notes.trim() || null,
      };

      if (editingId) {
        await updateLead(editingId, companyId, payload);
      } else {
        await createLead(payload);
      }

      resetForm();
      await loadLeads();
    } catch (error) {
      setMessage(error.message || "Failed to save lead");
    } finally {
      setSaving(false);
    }
  }, [companyId, form, editingId, loadLeads, resetForm]);

  const handleConvert = useCallback(
    async (leadId) => {
      if (!companyId || !leadId) {
        return;
      }

      setConvertingId(leadId);
      setMessage("");

      try {
        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();

        if (userError) {
          throw userError;
        }

        if (!user?.id) {
          throw new Error("User not found");
        }

        await convertLead(leadId, companyId, user.id);
        await loadLeads();
      } catch (error) {
        setMessage(error.message || "Failed to convert lead");
      } finally {
        setConvertingId("");
      }
    },
    [companyId, loadLeads]
  );

  const filteredLeads = useMemo(() => {
    const searchValue = search.trim().toLowerCase();

    return leads.filter((lead) => {
      const matchesSearch =
        !searchValue ||
        String(lead.lead_name || "")
          .toLowerCase()
          .includes(searchValue) ||
        String(lead.business_name || "")
          .toLowerCase()
          .includes(searchValue) ||
        String(lead.email || "")
          .toLowerCase()
          .includes(searchValue) ||
        String(lead.phone || "")
          .toLowerCase()
          .includes(searchValue);

      const matchesStatus =
        statusFilter === "all" || String(lead.status || "") === statusFilter;

      const matchesRating =
        ratingFilter === "all" || String(lead.rating || "") === ratingFilter;

      return matchesSearch && matchesStatus && matchesRating;
    });
  }, [leads, search, statusFilter, ratingFilter]);

  return (
    <div>
      <h2 className="crm-page-title">Leads</h2>

      {message ? <div className="crm-message">{message}</div> : null}

      <div className="crm-table-card" style={{ marginBottom: "20px" }}>
        <h3 className="crm-page-title" style={{ fontSize: "20px" }}>
          {editingId ? "Edit Lead" : "Create Lead"}
        </h3>

        <div className="crm-form-row">
          <div className="crm-form-group">
            <label>Lead Name</label>
            <input
              className="crm-input"
              value={form.lead_name}
              onChange={(event) =>
                handleFormChange("lead_name", event.target.value)
              }
            />
          </div>

          <div className="crm-form-group">
            <label>Business Name</label>
            <input
              className="crm-input"
              value={form.business_name}
              onChange={(event) =>
                handleFormChange("business_name", event.target.value)
              }
            />
          </div>
        </div>

        <div className="crm-form-row">
          <div className="crm-form-group">
            <label>Email</label>
            <input
              className="crm-input"
              value={form.email}
              onChange={(event) =>
                handleFormChange("email", event.target.value)
              }
            />
          </div>

          <div className="crm-form-group">
            <label>Phone</label>
            <input
              className="crm-input"
              value={form.phone}
              onChange={(event) =>
                handleFormChange("phone", event.target.value)
              }
            />
          </div>
        </div>

        <div className="crm-form-row">
          <div className="crm-form-group">
            <label>Industry</label>
            <input
              className="crm-input"
              value={form.industry}
              onChange={(event) =>
                handleFormChange("industry", event.target.value)
              }
            />
          </div>

          <div className="crm-form-group">
            <label>Lead Source</label>
            <input
              className="crm-input"
              value={form.lead_source}
              onChange={(event) =>
                handleFormChange("lead_source", event.target.value)
              }
            />
          </div>
        </div>

        <div className="crm-form-row">
          <div className="crm-form-group">
            <label>Estimated Value</label>
            <input
              className="crm-input"
              type="number"
              value={form.estimated_value}
              onChange={(event) =>
                handleFormChange("estimated_value", event.target.value)
              }
            />
          </div>

          <div className="crm-form-group">
            <label>Rating</label>
            <select
              className="crm-select"
              value={form.rating}
              onChange={(event) =>
                handleFormChange("rating", event.target.value)
              }
            >
              <option value="cold">Cold</option>
              <option value="warm">Warm</option>
              <option value="hot">Hot</option>
            </select>
          </div>
        </div>

        <div className="crm-form-group">
          <label>Qualification Notes</label>
          <textarea
            className="crm-textarea"
            rows={4}
            value={form.qualification_notes}
            onChange={(event) =>
              handleFormChange("qualification_notes", event.target.value)
            }
          />
        </div>

        <div className="crm-inline-actions">
          <button
            className="crm-primary-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : editingId ? "Update Lead" : "Add Lead"}
          </button>

          {editingId ? (
            <button
              className="crm-secondary-btn"
              onClick={resetForm}
              disabled={saving}
            >
              Cancel Edit
            </button>
          ) : null}
        </div>
      </div>

      <div className="crm-table-card" style={{ marginBottom: "20px" }}>
        <div className="crm-filter-row">
          <div className="crm-form-group">
            <label>Search</label>
            <input
              className="crm-input"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search leads"
            />
          </div>

          <div className="crm-form-group">
            <label>Status</label>
            <select
              className="crm-select"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="all">All</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="unqualified">Unqualified</option>
              <option value="converted">Converted</option>
            </select>
          </div>

          <div className="crm-form-group">
            <label>Rating</label>
            <select
              className="crm-select"
              value={ratingFilter}
              onChange={(event) => setRatingFilter(event.target.value)}
            >
              <option value="all">All</option>
              <option value="cold">Cold</option>
              <option value="warm">Warm</option>
              <option value="hot">Hot</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="crm-message">Loading leads...</div>
      ) : filteredLeads.length === 0 ? (
        <div className="crm-message">No leads found.</div>
      ) : (
        filteredLeads.map((lead) => (
          <div key={lead.id} className="crm-list-card">
            <h3 className="crm-list-title">
              {lead.lead_name || "Unnamed lead"}
            </h3>
            <div className="crm-list-subtext">
              Business: {lead.business_name || "N/A"}
            </div>
            <div className="crm-list-subtext">Email: {lead.email || "N/A"}</div>
            <div className="crm-list-subtext">Phone: {lead.phone || "N/A"}</div>
            <div className="crm-list-subtext">
              Status: {lead.status || "new"}
            </div>
            <div className="crm-list-subtext">
              Rating: {lead.rating || "cold"}
            </div>
            <div className="crm-list-subtext">
              Score: {Number(lead.score || 0)}
            </div>

            <div className="crm-inline-actions">
              <button
                className="crm-small-btn alt"
                onClick={() => handleEdit(lead)}
              >
                Edit
              </button>

              {lead.status !== "converted" ? (
                <button
                  className="crm-small-btn"
                  onClick={() => handleConvert(lead.id)}
                  disabled={convertingId === lead.id}
                >
                  {convertingId === lead.id ? "Converting..." : "Convert Lead"}
                </button>
              ) : null}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default LeadsPage;
